/**
 * Sentry Error Monitoring
 *
 * Initialise once at app startup (main.tsx).
 * User context is set in AuthContext after login.
 *
 * Packages: @sentry/react, @sentry/vite-plugin
 * Docs: https://docs.sentry.io/platforms/javascript/guides/react/
 */

import * as Sentry from "@sentry/react";

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  // Skip if DSN is not configured (local dev without Sentry credentials)
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info("[Sentry] VITE_SENTRY_DSN not set — monitoring disabled");
    }
    return;
  }

  Sentry.init({
    dsn,

    integrations: [
      // Automatic page-load + navigation performance traces
      Sentry.browserTracingIntegration(),

      // Session Replay: records DOM snapshots on error
      Sentry.replayIntegration({
        // Mask all text and media in replays (GDPR safe default)
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance tracing: 10 % in production, 100 % in development
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Replay sampling: 5 % normal sessions, 100 % of sessions with errors
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,

    // Tag every event with the current environment
    environment: import.meta.env.MODE,

    // Only send events when in production (avoids noise from localhost)
    // Comment out this line if you want to test Sentry locally
    enabled: import.meta.env.PROD,

    // Ignore common browser noise that's not actionable
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      "Network request failed",
      "Failed to fetch",
      "Load failed",
      "AbortError",
    ],

    // Drop errors originating from third-party ad network scripts (Adcash + partners)
    denyUrls: [
      /acscdn\.com/,
      /adcash\.com/,
      /adexchangeclear\.com/,
      /overbridgenet\.com/,
      /usrpubtrk\.com/,
      /vidwestxyz\.com/,
      /videoSliderAlt\.js/,
    ],
  });
}

// Re-export Sentry so callers don't need a second import
export { Sentry };
